import type { ComponentType, SVGProps } from 'react';
import { InlineError } from './BrowserPrimitives';
import { TABLE_CELL_PADDING, TABLE_ROW_HEIGHT } from './browserStyles';

const SKELETON_ROW_COUNT = 10;

export function TableSkeletonRows({ widths }: { widths: readonly string[] }) {
    return (
        <>
            {Array.from({ length: SKELETON_ROW_COUNT }, (_, row) => (
                <tr key={row} className={TABLE_ROW_HEIGHT}>
                    {widths.map((width, column) => (
                        <td
                            key={column}
                            className={`${TABLE_CELL_PADDING} border-b border-gray-200 align-middle`}
                        >
                            <div className={`h-3.5 rounded bg-gray-200 animate-pulse ${width}`} />
                        </td>
                    ))}
                </tr>
            ))}
        </>
    );
}

interface TableEmptyProps {
    icon: ComponentType<SVGProps<SVGSVGElement>>;
    message: string;
    onClear?: () => void;
}

export function TableEmpty({ icon: Icon, message, onClear }: TableEmptyProps) {
    return (
        <div className="flex flex-col items-center gap-3 text-center">
            <Icon className="h-10 w-10 text-gray-300" strokeWidth={1} aria-hidden="true" />
            <p className="text-sm text-gray-500">{message}</p>
            {onClear && (
                <button
                    type="button"
                    onClick={onClear}
                    className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
                >
                    Clear filters
                </button>
            )}
        </div>
    );
}

interface TableErrorProps {
    message: string;
    onRetry: () => void;
}

export function TableError({ message, onRetry }: TableErrorProps) {
    return (
        <div role="alert" className="flex flex-col items-center gap-3 text-center">
            <p className="text-sm text-red-500 font-medium max-w-[420px]">{message}</p>
            <button
                type="button"
                onClick={onRetry}
                className="text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg px-3 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
            >
                Retry
            </button>
        </div>
    );
}

// Shown in place of the scroll sentinel after a failed page, so it can't retry on its own.
export function TableLoadMoreError({ message, onRetry }: TableErrorProps) {
    return (
        <div className="shrink-0 border-t border-gray-100 px-5 py-2.5">
            <InlineError message={message} onRetry={onRetry} retryLabel="Try again" />
        </div>
    );
}
